<template>
  <AppLayout>
    <div class="agenda">
      <!-- HEADER SUPERIOR -->
      <header class="topbar">
        <h1>Agenda</h1>
        <button class="btn-menu" @click="abrirPerfil" aria-label="Abrir perfil">
          <span></span><span></span><span></span>
        </button>
      </header>

      <!-- LOADING Y ERROR -->
      <div v-if="turnosStore.loading" style="text-align:center; margin: 32px 0;">
        <span>Cargando turnos...</span>
      </div>
      <div v-if="turnosStore.error" style="color:#e74c3c; text-align:center; margin: 16px 0;">
        {{ turnosStore.error }}
        <br v-if="turnosStore.modoOffline" />
        <button
          v-if="turnosStore.modoOffline"
          @click="turnosStore.reintentarConexion()"
          style="margin-top:8px; padding:6px 16px; background:#678AB2; color:white; border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:13px;"
        >
          Reintentar conexión
        </button>
      </div>

      <!-- SIEMPRE MOSTRAR UI -->
      <!-- BARRA DE BOTONES -->
      <div class="vista-selector">
        <button
          :class="{ activo: vistaActual === 'dia' }"
          @click="vistaActual = 'dia'"
        >
          Día
        </button>
        <button
          :class="{ activo: vistaActual === 'semana' }"
          @click="vistaActual = 'semana'"
        >
          Semana
        </button>
        <button
          :class="{ activo: vistaActual === 'mes' }"
          @click="vistaActual = 'mes'"
        >
          Mes
        </button>
      </div>

      <!-- CONTENIDO SEGÚN VISTA -->
      <div v-if="vistaActual === 'dia'">
        <!-- Aquí va tu agenda del día -->
      </div>
      <CalendarioSemanal
        v-if="vistaActual === 'semana'"
        :turnos="turnos"
        @seleccionarSlot="preseleccionarTurno"
        @abrirTurno="abrirEstadoTurno"
        @moverTurno="moverTurno"
      />
      <CalendarioMensual
        v-if="vistaActual === 'mes'"
        :turnos="turnos"
        @abrirTurno="abrirEstadoTurno"
        @irADia="irADiaDesdeMes"
        @nuevaCita="agendarDesdeMes"
      />

      <!-- HEADER FECHA -->
      <header class="agenda-header sticky" v-if="vistaActual === 'dia'">
        <button @click="diaAnterior">‹</button>

        <div class="fecha fecha-clickable" @click="mostrarCalendario = true" title="Tocá para elegir una fecha">
          <span>{{ fechaFormateada }}</span>
        </div>

        <button @click="diaSiguiente">›</button>
      </header>

      <!-- Día que el usuario no atiende: se avisa una vez, arriba, en vez de repetirlo en
           cada hueco. Los turnos que ya existan ese día se siguen viendo y editando. -->
      <div v-if="vistaActual === 'dia' && !diaSeleccionadoActivo" class="aviso-dia-bloqueado">
        {{ avisoDiaBloqueado }}
      </div>

      <!-- BOTÓN EXPANDIR/COLAPSAR -->
      <div v-if="vistaActual === 'dia' && diaSeleccionadoActivo" class="vista-toggle">
        <button @click="vistaExpandida = !vistaExpandida" class="btn-action">
          <span v-if="vistaExpandida" class="btn-icon">−</span>
          <span v-else class="btn-icon">+</span>
          {{ vistaExpandida ? 'Solo agendados' : 'Mostrar disponibles' }}
        </button>
      </div>

      <!-- TURNOS DEL DÍA -->
      <div class="agenda-dia" v-if="vistaActual === 'dia'">
        <!-- Vista compacta: solo turnos agendados -->
        <template v-if="!mostrarExpandida">
          <div
            v-for="(turno, index) in turnosFiltrados"
            :key="turno.id || `turno-${index}`"
            class="turno-contenedor"
          >
            <div
              class="slot ocupado"
              :style="{
                borderLeft: `4px solid ${colorPorEstado(turno.estado)}`,
                boxShadow: turno.estado === 'en curso' ? '0 0 0 2px #f59e0b' : 'none'
              }"
              @click="abrirEstadoTurno(turno)"
            >
              <TurnoCard
                :inicio="turno.inicio"
                :fin="turno.fin"
                :paciente="turno.paciente"
                :motivo="turno.motivo"
                :pacienteId="turno.pacienteId"
                :estado="turno.estado"
                :tipo="turno.tipo"
                :id="turno.id"
                @cambiar-estado="onCambiarEstado(turno.id, $event)"
              />
            </div>
          </div>
          <div v-if="turnosFiltrados.length === 0" style="text-align:center; color:#888; margin:32px 0;">No hay turnos para este día.</div>
        </template>

        <!-- Vista expandida: todos los horarios -->
        <template v-else>
          <div
            v-for="slot in slotsDelDia"
            :key="slot.hora"
            :class="['slot', { 'ocupado': slot.turno, 'disponible': !slot.turno }]"
            :style="slot.turno ? {
              borderLeft: `4px solid ${colorPorEstado(slot.turno.estado)}`,
              boxShadow: slot.turno.estado === 'en curso' ? '0 0 0 2px #f59e0b' : 'none'
            } : {}"
            @click="slot.turno ? abrirEstadoTurno(slot.turno) : abrirNuevoTurno(slot.hora)"
          >
            <div v-if="slot.turno" class="turno-wrapper">
              <!-- Turno que quedó fuera del horario configurado (típico: importado de Google
                   Calendar). Se muestra igual —si no, sería invisible— pero avisando por qué. -->
              <span v-if="slot.fuera" class="aviso-fuera-horario">Fuera de tu horario</span>
              <TurnoCard
                :inicio="slot.turno.inicio"
                :fin="slot.turno.fin"
                :paciente="slot.turno.paciente"
                :motivo="slot.turno.motivo"
                :pacienteId="slot.turno.pacienteId"
                :estado="slot.turno.estado"
                :tipo="slot.turno.tipo"
                :id="slot.turno.id"
                @cambiar-estado="onCambiarEstado(slot.turno.id, $event)"
              />
            </div>
            <div v-else class="slot-vacio">
              <span class="hora-slot">{{ slot.hora }}</span>
              <span class="disponible-text">Disponible</span>
            </div>
          </div>
        </template>
      </div>

      <!-- BOTÓN NUEVO TURNO -->
      <button v-if="!showNuevoTurno && !showEstadoTurno" class="btn-plus" @click="abrirNuevoTurno">
        +
      </button>
    </div>
  </AppLayout>

  <!-- MODALES -->
  <NuevoTurno
    v-if="showNuevoTurno"
    :fecha="turnoPreseleccionado.fecha"
    :inicio="turnoPreseleccionado.inicio"
    :turnos="turnos"
    :pacientePredefinido="pacientePreseleccionadoNombre"
    :pacienteIdPredefinido="pacientePreseleccionadoId"
    :turnoEditar="turnoEditar"
    @cerrar="cerrarNuevoTurno"
    @guardar="agregarTurno"
  />

  <EstadoTurno
    v-if="showEstadoTurno"
    :turno="turnoSeleccionado"
    @cerrar="showEstadoTurno = false"
    @cambiarEstado="cambiarEstadoTurno"
    @eliminar="eliminarTurno"
    @guardar="actualizarTurno"
    @editar="iniciarEdicionTurno"
  />

  <ToastNotification
    v-if="toast"
    :message="toast.message"
    :type="toast.type"
    @close="toast = null"
  />

  <!-- Calendario propio (estilo de la app) para saltar a una fecha en la vista Día -->
  <Teleport to="body">
    <div v-if="mostrarCalendario" class="cal-overlay" @click="mostrarCalendario = false">
      <div class="cal-popover" @click.stop>
        <SelectorFechaMini :modelValue="fechaSeleccionada" @seleccionar="onSeleccionarFecha" />
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import NuevoTurno from "@/components/NuevoTurno.vue";
import EstadoTurno from "@/components/EstadoTurno.vue";
import TurnoCard from "@/components/TurnoCard.vue";
import CalendarioSemanal from "@/components/CalendarioSemanal.vue";
import CalendarioMensual from "@/components/CalendarioMensual.vue";
import SelectorFechaMini from "@/components/SelectorFechaMini.vue";
import AppLayout from "@/layouts/AppLayout.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { ref, computed, onMounted } from "vue";
import { usePerfilPanel } from "@/composables/usePerfilPanel";
const { abrir: abrirPerfil } = usePerfilPanel();
import { useTurnosStore } from "@/stores/turnos";
import { usePacientesStore } from "@/stores/pacientes";
import { useRoute } from "vue-router";
import { useGoogleCalendar } from "@/composables/useGoogleCalendar";
import { useScrollLock } from "@/composables/useScrollLock";
import { turnoCubre, diaEnPlural } from "@/utils/horarios";
import { useHorarioAgenda } from "@/composables/useHorarioAgenda";

/* ---------------------------
   ESTADOS
---------------------------- */
const route = useRoute();
const turnosStore = useTurnosStore();
const pacientesStore = usePacientesStore();
const { conectado: gcalConectado, exportarTurnoACalendar } = useGoogleCalendar();
const { slotsPara, estaFueraDeHorario, esDiaActivo } = useHorarioAgenda();
const vistaActual = ref("dia");
const showNuevoTurno = ref(false);
const showEstadoTurno = ref(false);

const turnoPreseleccionado = ref({ fecha: null, inicio: null });
const turnoSeleccionado = ref(null);
const turnoEditar = ref(null);
const toast = ref(null);
const pacientePreseleccionadoNombre = ref("");
// Id del paciente preseleccionado (cuando se agenda desde su ficha). Con esto NuevoTurno
// arranca ya ligado a la ficha exacta, sin depender del nombre (homónimos).
const pacientePreseleccionadoId = ref("");

function showToast(message, type = 'success') {
  toast.value = { message, type };
}

function abrirNuevoTurno(hora = null) {
  // Se corta acá y no al guardar: abrir el modal, hacerle escribir el paciente y recién
  // entonces rebotarlo sería trabajo tirado.
  if (!diaSeleccionadoActivo.value) {
    showToast(avisoDiaBloqueado.value, "error");
    return;
  }
  turnoPreseleccionado.value = {
    fecha: (() => { const f = fechaSeleccionada.value; return `${f.getFullYear()}-${String(f.getMonth()+1).padStart(2,'0')}-${String(f.getDate()).padStart(2,'0')}`; })(),
    inicio: hora, // si viene de un slot, se completa; si no, queda null
  };
  showNuevoTurno.value = true;
}

function iniciarEdicionTurno(turno) {
  showEstadoTurno.value = false;
  turnoEditar.value = turno;
  showNuevoTurno.value = true;
}

function cerrarNuevoTurno() {
  showNuevoTurno.value = false;
  turnoEditar.value = null;
  turnoPreseleccionado.value = { fecha: null, inicio: null };
  pacientePreseleccionadoNombre.value = '';
  pacientePreseleccionadoId.value = '';
}

function preseleccionarTurno({ fecha, hora }) {
  // Verificar si el slot seleccionado ya está ocupado por un turno existente (excluyendo eliminados)
  const turnoExistente = turnos.value.find((t) => {
    return t.fecha === fecha && t.inicio && t.fin && t.inicio <= hora && hora < t.fin && t.estado !== 'eliminada';
  });

  if (turnoExistente) {
    abrirEstadoTurno(turnoExistente);
    return;
  }

  turnoPreseleccionado.value = {
    fecha,
    inicio: hora
  };
  showNuevoTurno.value = true;
}

function abrirEstadoTurno(turno) {
  // Buscar datos actualizados del paciente (como el teléfono) para WhatsApp
  const paciente = pacientesStore.pacientes.find((p) => p.id === turno.pacienteId);
  turnoSeleccionado.value = {
    ...turno,
    telefono: paciente ? paciente.telefono : null
  };
  showEstadoTurno.value = true;
}

/* ---------------------------
   FECHA Y TURNOS
---------------------------- */
const fechaSeleccionada = ref(new Date());

const fechaFormateada = computed(() => {
  const f = fechaSeleccionada.value;
  const texto = f.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
});

// Calendario propio (estilo de la app) para saltar a una fecha en la vista Día.
const mostrarCalendario = ref(false);
useScrollLock(() => mostrarCalendario.value);
function onSeleccionarFecha(d) {
  fechaSeleccionada.value = d;
  mostrarCalendario.value = false;
}

// Desde el calendario mensual: ir a la vista Día de la fecha tocada.
function irADiaDesdeMes(fechaStr) {
  const [y, m, d] = fechaStr.split('-').map(Number);
  fechaSeleccionada.value = new Date(y, m - 1, d);
  vistaActual.value = 'dia';
}

// Desde el calendario mensual: abrir nueva cita con la fecha (y hora si vino de un slot libre).
// No se toca pacientePreseleccionadoNombre: si venimos desde la ficha de un paciente, ese
// nombre debe mantenerse (y queda bloqueado en NuevoTurno). Si no, ya está vacío.
function agendarDesdeMes({ fecha, hora }) {
  turnoPreseleccionado.value = { fecha, inicio: hora || null };
  showNuevoTurno.value = true;
}

const turnos = computed(() => turnosStore.turnos);

function cargarTurnosDesdeLocalStorage() {
  turnosStore.cargarDesdeLocalStorage();
  pacientesStore.cargarDesdeLocalStorage();
}

onMounted(() => {
  cargarTurnosDesdeLocalStorage();
  
  if (route.query.view) {
    vistaActual.value = route.query.view;
  }
  if (route.query.paciente) {
    pacientePreseleccionadoNombre.value = route.query.paciente;
  }
  if (route.query.pacienteId) {
    pacientePreseleccionadoId.value = route.query.pacienteId;
  }
});

const vistaExpandida = ref(false);

const turnosFiltrados = computed(() => {
  const f = fechaSeleccionada.value;
  const fechaVistaStr = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
  return turnos.value
    .filter((t) => t.fecha === fechaVistaStr && t.estado !== 'eliminada')
    .sort((a, b) => {
      // Ordenar por hora de inicio (formato "HH:MM")
      const inicioA = a.inicio || '';
      const inicioB = b.inicio || '';
      return inicioA.localeCompare(inicioB);
    });
});

// Combinar slots con turnos para vista expandida.
// El rango sale del horario de trabajo que el usuario configuró en Ajustes (por defecto,
// el de siempre: 09:00 a 22:30).
const slotsDelDia = computed(() => {
  const f = fechaSeleccionada.value;
  const fechaVistaStr = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;

  const delDia = turnos.value.filter(t => t.fecha === fechaVistaStr && t.estado !== 'eliminada');

  // Se muestra un slot solo si hay un turno que empieza ahí, o si está realmente libre.
  // Los slots CUBIERTOS por una cita más larga (ej: 9:30 dentro de un turno de 9 a 10) se
  // OCULTAN: no son agendables y la tarjeta del turno ya ocupa ese espacio visualmente.
  // En un día que el usuario bloqueó no se puede agendar, así que no se listan los huecos
  // libres: mostrar "Disponible" en un día donde Guardar va a rebotar sería mentirle.
  const diaBloqueado = !esDiaActivo(fechaVistaStr);

  const slots = [];
  for (const hora of slotsPara(delDia)) {
    const turno = delDia.find(t => t.inicio === hora) || null;
    if (!turno && (diaBloqueado || delDia.some(t => turnoCubre(t, hora)))) continue;
    slots.push({ hora, turno, fuera: estaFueraDeHorario(hora) });
  }
  return slots;
});

// El día que se está mirando, ¿es uno de los que el usuario atiende?
const diaSeleccionadoActivo = computed(() => {
  const f = fechaSeleccionada.value;
  return esDiaActivo(`${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`);
});

const avisoDiaBloqueado = computed(() =>
  `No atendés ${diaEnPlural(fechaSeleccionada.value)}. Podés cambiarlo en Ajustes › Horario de trabajo.`
);

// En un día bloqueado se cae siempre a la vista compacta (lista de turnos): la expandida
// existe para elegir un hueco libre, y ahí no hay huecos que elegir. Si no, el usuario que
// dejó activada la vista expandida veía una pantalla vacía sin entender por qué.
const mostrarExpandida = computed(() => vistaExpandida.value && diaSeleccionadoActivo.value);

// `onDone(exito, mensaje)` lo manda NuevoTurno para enterarse del resultado REAL. Antes el modal
// cantaba "turno guardado" ni bien emitía, sin esperar a la nube: si el guardado fallaba (p. ej.
// no se podía crear el paciente), la doctora veía "éxito" y el turno no existía.
async function agregarTurno(turno, onDone = () => {}) {
  // Bloqueo de día completo con "repetir hasta": NuevoTurno manda la lista entera para que se
  // guarde de una sola vez y el usuario vea un solo aviso en vez de uno por día.
  if (Array.isArray(turno)) {
    const fallaron = turno.filter((b) => !turnosStore.agregarTurno(b)).length;
    if (fallaron === 0) onDone(true);
    else onDone(false, `No se pudieron guardar ${fallaron} de los ${turno.length} días. Revisá la conexión.`);
    return;
  }

  try {
    // Si el turno ya existe en el store, es una edición
    const existe = turnosStore.turnos.find(t => String(t.id) === String(turno.id));
    if (existe) {
      turnosStore.actualizarTurno(turno.id, {
        fecha: turno.fecha,
        inicio: turno.inicio,
        fin: turno.fin,
        paciente: turno.paciente,
        pacienteId: turno.pacienteId,
        motivo: turno.motivo,
      });
      if (turno.pacienteId) {
        pacientesStore.actualizarTurno(turno.pacienteId, turno.id, {
          fecha: turno.fecha,
          inicio: turno.inicio,
          fin: turno.fin,
          motivo: turno.motivo,
        });
      }
      if (gcalConectado.value && existe.googleEventId) {
        exportarTurnoACalendar({ ...existe, ...turno });
      }
      onDone(true);
      return;
    }

    // Bloqueo / descanso: no tiene paciente ni ficha, solo ocupa el horario de la agenda.
    // Se guarda directo en la colección de turnos (una sola escritura, sin denormalizar).
    if (turno.tipo === 'bloqueo') {
      const ok = turnosStore.agregarTurno(turno);
      onDone(!!ok, ok ? "" : "No se pudo guardar el bloqueo. Revisá la conexión.");
      return;
    }

    // Si el turno ya trae el id de un paciente REAL (la doctora lo eligió en el buscador o
    // vino desde su ficha), usar ESE — así dos homónimos ("María González") no se confunden.
    // Solo si el id es un placeholder (freehand: NuevoTurno pone Date.now(), que nunca calza
    // con un id de paciente) resolvemos/creamos por nombre, como antes.
    let paciente = pacientesStore.pacientes.find(
      (p) => String(p.id) === String(turno.pacienteId),
    );
    if (!paciente) {
      // Crear o obtener el paciente por nombre (espera la confirmación en la nube; null si falla)
      paciente = await pacientesStore.crearPacienteObtenerExistente(turno.paciente);
    }
    if (!paciente) {
      // Sin usuario o sin guardar en la nube: no creamos un turno huérfano.
      onDone(false, "No se pudo crear el paciente en la nube, así que el turno no se agendó. Revisá la conexión e intentá de nuevo.");
      return;
    }

    // Asegurar el pacienteId correcto en el turno.
    turno.pacienteId = paciente.id;

    // Crear el turno de forma ATÓMICA en la Agenda y en la ficha del paciente (P1-2):
    // o se guarda en los dos lados, o en ninguno (nunca desincronizados).
    const ok = await turnosStore.agregarTurnoConFicha(turno, paciente.id);
    onDone(!!ok, ok ? "" : "No se pudo guardar el turno en la nube. Revisá la conexión e intentá de nuevo.");
  } catch (e) {
    onDone(false, e?.message || "No se pudo guardar el turno.");
  }
}



function cambiarEstadoTurno({ id, estado }) {
  // 1. Actualizar en la Agenda general
  turnosStore.cambiarEstadoTurno(id, estado);
  // 2. Actualizar en el historial del Paciente
  if (turnoSeleccionado.value && turnoSeleccionado.value.pacienteId) {
    pacientesStore.actualizarTurno(turnoSeleccionado.value.pacienteId, id, { estado });
  }
  // 3. Actualizar el estado en el modal para ver el cambio sin cerrar
  if (turnoSeleccionado.value) turnoSeleccionado.value.estado = estado;
}

function actualizarTurno({ id, motivo }) {
  // 1. Actualizar en la Agenda general
  turnosStore.actualizarTurno(id, { motivo }); 
  
  // 2. Actualizar en el historial del Paciente (para que se vea en su ficha)
  if (turnoSeleccionado.value && turnoSeleccionado.value.pacienteId) {
    pacientesStore.actualizarTurno(turnoSeleccionado.value.pacienteId, id, { motivo });
  }

  if (turnoSeleccionado.value) turnoSeleccionado.value.motivo = motivo;
  showToast("Turno actualizado correctamente");
}

function eliminarTurno(id) {
  // 1. Cambiar estado a "eliminada" en la Agenda general
  turnosStore.cambiarEstadoTurno(id, "eliminada");
  
  // 2. Actualizar en el historial del Paciente
  if (turnoSeleccionado.value && turnoSeleccionado.value.pacienteId) {
    pacientesStore.actualizarTurno(turnoSeleccionado.value.pacienteId, id, { estado: "eliminada" });
  }
  
  showEstadoTurno.value = false;
  turnoSeleccionado.value = null;
  showToast("Cita marcada como eliminada");
}

function moverTurno({ id, fecha, hora }) {
  const turno = turnosStore.turnos.find(t => String(t.id) === String(id));
  if (!turno) return;

  const [hI, mI] = turno.inicio.split(':').map(Number);
  const [hF, mF] = turno.fin.split(':').map(Number);
  let duracion = (hF * 60 + mF) - (hI * 60 + mI);
  if (!(duracion > 0)) duracion = 30;

  const [hNuevo, mNuevo] = hora.split(':').map(Number);
  const nuevoInicioMin = hNuevo * 60 + mNuevo;
  const nuevoFinMin = nuevoInicioMin + duracion;
  const nuevoFin = `${String(Math.floor(nuevoFinMin / 60)).padStart(2, '0')}:${String(nuevoFinMin % 60).padStart(2, '0')}`;

  // Bloquear si el nuevo horario se SUPERPONE con otra cita del mismo día. No alcanza con
  // comparar la hora de inicio: una cita de 60/90 min puede pisar a otra aunque empiecen
  // en horas distintas. Dos intervalos se solapan si: inicioA < finB && inicioB < finA.
  const aMinutos = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    return h * 60 + m;
  };
  const conflicto = turnosStore.turnos.find(t => {
    if (String(t.id) === String(id)) return false;
    // Ignorar citas eliminadas: no se muestran, no deben bloquear el movimiento.
    if (t.estado === 'eliminada') return false;
    if ((t.fecha || '').split('T')[0] !== fecha) return false;
    if (!t.inicio || !t.fin) return false;
    return nuevoInicioMin < aMinutos(t.fin) && aMinutos(t.inicio) < nuevoFinMin;
  });
  if (conflicto) {
    const quien = conflicto.paciente ? ` (${conflicto.paciente})` : "";
    showToast(`Se superpone con la cita de ${conflicto.inicio}${quien}`, "error");
    return;
  }

  turnosStore.actualizarTurno(id, { fecha, inicio: hora, fin: nuevoFin });

  if (turno.pacienteId) {
    pacientesStore.actualizarTurno(turno.pacienteId, id, { fecha, inicio: hora, fin: nuevoFin });
  }

  if (gcalConectado.value && turno.googleEventId) {
    exportarTurnoACalendar({ ...turno, fecha, inicio: hora, fin: nuevoFin });
  }

  showToast("Cita movida correctamente");
}

function diaAnterior() {
  const d = new Date(fechaSeleccionada.value);
  d.setDate(d.getDate() - 1);
  fechaSeleccionada.value = d;
}

function diaSiguiente() {
  const d = new Date(fechaSeleccionada.value);
  d.setDate(d.getDate() + 1);
  fechaSeleccionada.value = d;
}

/* ---------------------------
   (slots y slotsConTurno removidos, ahora mostramos turnosFiltrados directamente)
---------------------------- */

const COLORES_ESTADO = {
  'pendiente':  '#678AB2',
  'confirmado': '#22c55e',
  'en curso':   '#f59e0b',
  'atendido':   '#a855f7',
  'no asiste':  '#92400e',
  'anulado':    '#ef4444',
  'bloqueo':    '#9ca3af',
};

function colorPorEstado(estado) {
  return COLORES_ESTADO[estado] || '#678AB2';
}

function onCambiarEstado(id, estado) {
  cambiarEstadoTurno({ id, estado });
}
</script>

<style scoped>
.agenda {
  padding: 16px;
  padding-bottom: 80px;
  max-width: 420px;
  margin: auto;
  font-family: system-ui, sans-serif;
}

/* HEADER SUPERIOR */
.topbar {
  position: relative;
  height: 56px;
  background: #678AB2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 12px;
  margin-bottom: 12px;
}

.btn-menu {
  position: absolute;
  right: 16px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
}

.btn-menu:hover {
  background: rgba(255, 255, 255, 0.3);
}

.btn-menu span {
  display: block;
  width: 16px;
  height: 2px;
  background: white;
  border-radius: 2px;
}

.topbar h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

/* HEADER FECHA */
.agenda-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  margin: 16px auto;
}

.agenda-header.sticky {
  position: sticky;
  top: 0;
  z-index: 100;
}

.agenda-header button {
  background: none;
  border: none;
  font-size: 22px;
  color: #678AB2;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.fecha {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

/* Fecha clickeable (negra, formato de la app): abre el calendario propio */
.fecha-clickable {
  cursor: pointer;
}
.fecha-clickable:hover {
  opacity: 0.85;
}

/* Popup del calendario propio */
.cal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 3000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 90px;
}
.cal-popover {
  animation: cal-aparece 0.15s ease-out;
}
@keyframes cal-aparece {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* TURNOS DEL DÍA */
.agenda-dia {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.slot {
  display: flex;
  align-items: stretch;
  background: transparent;
  border-radius: 15px;
  padding: 0px;
  box-shadow: none;
  overflow: hidden; /* Para que el borde redondeado respete el borde de color */
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.slot.ocupado {
  background: #eaf2ff;
}

.slot.disponible {
  background: #e3f0fc;
  border: 1px dashed #8fbce6;
}

.slot.disponible:hover {
  background: #d2e6f9;
  border-color: #678AB2;
}

.slot-vacio {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
}

.hora-slot {
  font-weight: 600;
  color: #6b7280;
  font-size: 14px;
}

.disponible-text {
  font-size: 13px;
  color: #3b78bd;
  font-weight: 600;
}

.turno-wrapper {
  width: 100%;
  display: flex;
  /* Solo entra en juego cuando además está el aviso de "fuera de horario": lo deja en una
     línea propia arriba de la tarjeta, sin tocar el layout del caso normal. */
  flex-wrap: wrap;
}

.aviso-dia-bloqueado {
  margin: 12px 16px 0;
  padding: 10px 14px;
  background: #f1f2f4;
  border-left: 3px solid #9ca3af;
  border-radius: 0 8px 8px 0;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.4;
}

.aviso-fuera-horario {
  flex: 0 0 100%;
  background: #fef3c7;
  color: #92400e;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 10px;
}

/* BOTÓN TOGGLE VISTA */
.vista-toggle {
  display: flex;
  justify-content: flex-start;
  margin: 12px 16px;
}

.btn-action {
  background: #eaf1ff;
  color: #678AB2;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.btn-action:hover {
  background: #dbeafe;
}

.btn-icon {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
}

.hora {
  width: 60px;
  font-weight: 600;
  color: #678AB2;
}

.contenido {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.contenido strong {
  font-size: 14px;
}

.contenido span {
  font-size: 13px;
  color: #666;
}

.libre {
  color: #aaa;
  font-style: italic;
}

/* BOTÓN + */
.btn-plus {
  position: fixed;
  bottom: 120px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #678AB2;
  color: white;
  font-size: 28px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(103, 138, 178, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2001;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.vista-selector {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  margin: 16px auto;
  padding: 12px 16px;
  background: transparent;
  border-radius: 12px;
}

.vista-selector button {
  flex: 1;
  padding: 10px 16px;
  background: white;
  color: black;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.vista-selector button:hover {
  background: #678AB2;
  color: white;
  border-color: #678AB2;
}

.vista-selector button.activo {
  background: #678AB2;
  color: white;
  border-color: #678AB2;
}

.card {
  background: white;
  color: black;
  transition: all 0.25s ease;
  cursor: pointer;
}

.card:hover,
.card.activo {
  background: #678AB2;
  color: white;
}

/* RESPONSIVE DESIGN */
/* Tablet (768px - 1024px) */
@media (min-width: 768px) {
  .agenda {
    padding: 24px;
    padding-bottom: 80px;
    max-width: 800px;
  }

  .topbar h1 {
    font-size: 24px;
    font-weight: 700;
  }

  .fecha {
    font-size: 16px;
  }

  .vista-selector {
    padding: 16px 24px;
  }

  .vista-selector button {
    padding: 12px 20px;
    font-size: 16px;
  }
}

.turno-contenedor {
  position: relative;
  display: flex;
  flex-direction: column;
}

.badge-en-curso-ext {
  position: absolute;
  top: -9px;
  right: 10px;
  z-index: 2;
  background: #f59e0b;
  color: white;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 10px;
  border-radius: 20px;
}

/* Desktop (1024px+) */
@media (min-width: 1024px) {
  .agenda {
    padding: 32px;
    padding-bottom: 80px;
    max-width: 1200px;
  }

  .topbar {
    height: 64px;
    margin-bottom: 20px;
  }

  .topbar h1 {
    font-size: 28px;
    font-weight: 700;
  }

  .fecha {
    font-size: 18px;
  }

  .vista-selector {
    padding: 20px 32px;
    margin: 24px auto;
  }

  .vista-selector button {
    padding: 14px 24px;
    font-size: 16px;
  }

  .agenda-header button {
    font-size: 26px;
  }
}
</style>
